import { useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItemButton from "@mui/material/ListItemButton";
import { Link, useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import Logo from "../../assets/logo.svg";
import SidebarList from "./SidebarList";

const Sidebar = ({ open, handleDrawerClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:767px)");
  const drawerWidth = isMobile ? 210 : 280;
  const [activeButton, setActiveButton] = useState(null);

  // Get user permissions and role from Redux state
  const { userData } = useSelector((state) => state.auth);
  const userPermissions = userData?.user?.permissions?.map(Number) || [];
  const userRole = userData?.user?.role || "";

  // Get filtered sidebar items using the function, passing both permissions and role
  const sidebarItems = SidebarList(userPermissions, userRole);

  useEffect(() => {
    setActiveButton(location.pathname);
  }, [location.pathname]);

  // Styled components from your Main.js
  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  }));

  const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const closedMixin = (theme) => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
  });

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",

    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  }));

  return (
    <Drawer variant="permanent" open={open} anchor="right">
      <DrawerHeader
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: drawerWidth,
          mt: isMobile ? "6px" : "10px",
        }}
      >
        <img src={Logo} alt="" />
        <IconButton onClick={handleDrawerClose}>
          {theme.direction === "rtl" ? (
            <ChevronLeftIcon />
          ) : (
            <ChevronRightIcon />
          )}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List>
        {sidebarItems.map((item, index) => (
          <ListItemButton
            component={Link}
            to={item.path}
            onClick={() => setActiveButton(item.path)}
            className={
              activeButton === item.path ? "list-item active" : "list-item"
            }
            key={index + 1}
          >
            {item.icon}
            {open && item.label}
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;
